import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  // min-h-[38px] (SL-0027, Activity 4, Task 4.1): the July mockup's own `.btn` base. The label is
  // 600, not 700: Brand Kit 1.0.0 gives 700 to titles and main results (decision 52) and reads a
  // button label as the heading of its control. Disabled is the variant at 50% (§ CTA And
  // Buttons). UI refinement: a broader transition than color alone, and a consistent keyboard
  // focus ring so every Button shows the same accessible focus state — motion is honored by the
  // global reduce block.
  'inline-flex min-h-[38px] items-center justify-center gap-2 rounded-md text-sm font-semibold whitespace-nowrap transition-[color,background-color,border-color,box-shadow,opacity] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        // Brand Kit § CTA And Buttons: the action colour, with hover and pressed DERIVED from it,
        // so a new action colour needs no edit here. Level 0 at rest and on hover: no shadow.
        default:
          'bg-primary text-primary-foreground hover:bg-primary-hover active:bg-primary-pressed',
        // Secondary: the surface, ink, and a 1px chip border — an alternative that stays quieter
        // than the primary beside it.
        outline: 'border border-chip-border bg-card text-foreground hover:bg-accent',
        // Quiet: toolbars and icon buttons. Secondary ink, `--subtle` on hover.
        ghost: 'bg-transparent text-muted-foreground hover:bg-accent hover:text-foreground',
        // Brand Kit § CTA And Buttons: a destructive action uses the Error colour, distinct from the
        // primary CTA's Coral, and is ALWAYS paired with an icon + explicit label at the call site
        // (never distinguished by colour alone).
        destructive: 'bg-destructive text-destructive-foreground hover:opacity-90 hover:shadow-sm',
        // The quiet form of a destructive choice that sits beside a filled primary: an outline in
        // the Error colour, so the primary stays the heaviest button (F2b).
        'outline-destructive':
          'border border-destructive bg-card text-destructive hover:bg-destructive/10',
      },
      size: {
        default: 'h-9 px-4 py-2',
        sm: 'h-8 rounded-md px-3 text-xs',
        lg: 'h-10 rounded-md px-8',
        // 44×44px touch-target minimum (SL-0027, Activity 3, Task 3.2) — used by the mobile
        // hamburger nav trigger. Restyled (colors only) by Activity 4's Task 4.1; this `size` key
        // itself must not change again there.
        icon: 'size-11',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    // `data-slot` names the element as a button whatever it renders (a link, with `asChild`), so a
    // stylesheet can size every button of an area without listing tags.
    return (
      <Comp
        data-slot="button"
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';
